import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";

/**
 * Modern Slot Title Input with Customer Autocomplete
 *
 * Format: "Kundenname // Tätigkeit"
 * - Autocomplete für Kunden vor dem "//"
 * - Customer ID wird separat gespeichert
 * - Moderne UX mit Inline-Autocomplete
 */
const SlotTitleInputWithCustomer = ({ value, onChange, onCustomerChange, customerId }) => {
    const [customers, setCustomers] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [inputValue, setInputValue] = useState(value || "");
    const [cursorPosition, setCursorPosition] = useState(0);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [pendingSearch, setPendingSearch] = useState(null); // Store search while loading

    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const Auth = useAuthentication();

    useEffect(() => {
        loadCustomers();
    }, []);

    // Initialize with existing customer
    useEffect(() => {
        if (customerId && customers.length > 0) {
            const customer = customers.find(c => c.id === parseInt(customerId));
            if (customer) {
                setSelectedCustomer(customer);
            }
        }
    }, [customerId, customers]);

    // Parse value to extract customer and activity
    useEffect(() => {
        setInputValue(value || "");

        // If value contains "//" and we have a customer selected, update display
        if (value && value.includes(" // ") && selectedCustomer) {
            const parts = value.split(" // ");
            if (parts.length >= 2) {
                // Value already formatted correctly
            }
        }
    }, [value]);

    const loadCustomers = () => {
        setLoadingCustomers(true);
        axios({
            method: "get",
            url: SyncConstants["syncBaseURL"] + "customers",
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data && response.data.success) {
                setCustomers(response.data.data || []);
            }
            setLoadingCustomers(false);
        }).catch(error => {
            console.error("Error loading customers:", error);
            setLoadingCustomers(false);
        });
    };

    // Effect to show suggestions if customers loaded while user is typing
    useEffect(() => {
        if (!loadingCustomers && customers.length > 0 && pendingSearch) {
            const filtered = customers.filter(customer =>
                customer.title.toLowerCase().includes(pendingSearch.toLowerCase())
            );
            setFilteredCustomers(filtered);
            if (filtered.length > 0) {
                setShowSuggestions(true);
            }
            setPendingSearch(null);
        }
    }, [loadingCustomers, customers, pendingSearch]);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        const cursorPos = e.target.selectionStart;

        setInputValue(newValue);
        setCursorPosition(cursorPos);
        onChange(newValue);

        // Check if we're before the "//" separator or no separator exists yet
        const separatorIndex = newValue.indexOf(" // ");
        const isBeforeSeparator = separatorIndex === -1 || cursorPos <= separatorIndex;

        if (isBeforeSeparator) {
            // Extract the part before cursor for autocomplete
            const textBeforeCursor = separatorIndex === -1
                ? newValue.substring(0, cursorPos)
                : newValue.substring(0, separatorIndex);

            if (textBeforeCursor.length > 0) {
                // If customers are still loading, save search for later
                if (loadingCustomers || customers.length === 0) {
                    setPendingSearch(textBeforeCursor);
                    setShowSuggestions(false);
                } else {
                    const filtered = customers.filter(customer =>
                        customer.title.toLowerCase().includes(textBeforeCursor.toLowerCase())
                    );
                    setFilteredCustomers(filtered);
                    setShowSuggestions(filtered.length > 0);
                }
            } else {
                setShowSuggestions(false);
                setPendingSearch(null);
            }
        } else {
            setShowSuggestions(false);
            setPendingSearch(null);
        }

        // If user deletes the customer part, clear customer selection
        if (separatorIndex === -1 && selectedCustomer) {
            setSelectedCustomer(null);
            onCustomerChange(null);
        }
    };

    const selectCustomer = (customer) => {
        setSelectedCustomer(customer);
        onCustomerChange(customer.id);

        // Format: "Kundenname // Tätigkeit"
        const currentValue = inputValue;
        const separatorIndex = currentValue.indexOf(" // ");

        let newValue;
        if (separatorIndex === -1) {
            // No separator yet - add customer name and separator
            newValue = `${customer.title} // `;
        } else {
            // Replace customer part, keep activity
            const activity = currentValue.substring(separatorIndex + 4);
            newValue = `${customer.title} // ${activity}`;
        }

        setInputValue(newValue);
        onChange(newValue);
        setShowSuggestions(false);

        // Focus back on input and position cursor after separator
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                const cursorPos = customer.title.length + 4; // After " // "
                inputRef.current.setSelectionRange(cursorPos, cursorPos);
            }
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="position-relative">
            <input
                ref={inputRef}
                type="text"
                className="form-control"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Kundenname // Tätigkeit (z.B. 'Acme Corp // Meeting')"
            />

            {/* Customer Badge */}
            {selectedCustomer && (
                <div className="mt-2">
                    <span className="badge bg-success">
                        <i className="bi bi-building me-1"></i>
                        {selectedCustomer.title}
                    </span>
                    <small className="text-muted ms-2">
                        Kunde ausgewählt
                    </small>
                </div>
            )}

            {/* Loading indicator */}
            {loadingCustomers && pendingSearch && (
                <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" style={{ zIndex: 1000 }}>
                    <div className="p-3 text-center">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span className="visually-hidden">Lade Kunden...</span>
                        </div>
                        <small className="text-muted">Lade Kunden...</small>
                    </div>
                </div>
            )}

            {/* Autocomplete Suggestions */}
            {!loadingCustomers && showSuggestions && filteredCustomers.length > 0 && (
                <div
                    ref={suggestionsRef}
                    className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                    style={{
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }}
                >
                    <div className="p-2">
                        <small className="text-muted">
                            <i className="bi bi-search me-1"></i>
                            Kunden ({filteredCustomers.length})
                        </small>
                    </div>
                    <div className="list-group list-group-flush">
                        {filteredCustomers.map(customer => (
                            <button
                                type="button"
                                key={customer.id}
                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                onClick={() => selectCustomer(customer)}
                            >
                                <div>
                                    <strong>{customer.title}</strong>
                                    {customer.description && (
                                        <div className="small text-muted text-truncate" style={{ maxWidth: '400px' }}>
                                            {customer.description}
                                        </div>
                                    )}
                                </div>
                                {Boolean(customer.weclapp_customer_id) && (
                                    <span className="badge bg-info bg-opacity-10 text-info">
                                        <i className="bi bi-cloud-check"></i>
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* No Customers Found */}
            {!loadingCustomers && showSuggestions && filteredCustomers.length === 0 && (
                <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" style={{ zIndex: 1000 }}>
                    <div className="p-3 text-center">
                        <i className="bi bi-search fs-3 text-muted mb-2 d-block"></i>
                        <p className="text-muted mb-2">Kein Kunde gefunden</p>
                        <small className="text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Bitte wende dich an Teamleiter:innen, um neue Kunden zu importieren
                        </small>
                    </div>
                </div>
            )}

            {/* Help Text */}
            <small className="form-text text-muted d-block mt-1">
                <i className="bi bi-info-circle me-1"></i>
                Tippen Sie den Kundennamen, wählen Sie aus der Liste, und ergänzen Sie die Tätigkeit nach dem " // "
            </small>
        </div>
    );
};

export default SlotTitleInputWithCustomer;
